import { BATTLE_ROYALE, LANDMASSES, LANTERN_ORIGIN, LANTERN_PATHS, LANTERN_SITES, OUTPOSTS, ROCKS, SKULL_ISLE, SKULL_ORIGIN, SKULL_PATHS, SKULL_TERRACES, SKULL_TOWERS, WORLD, coastPolygon, getIsland, mesaContour, nearestOutpost, type IslandId, type Landmark } from "@tiny-seas/shared";

/**
 * Maps are drawn from the shared world data (coastlines, landmarks, dig
 * spots), so they always match the 3D world. SVG y points down; world z
 * points north (up on the map), hence the `-z`.
 */

const SVG_NS = "http://www.w3.org/2000/svg";

function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, parent?: Element): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  parent?.appendChild(e);
  return e;
}

function outlinePath(l: (typeof LANDMASSES)[number], points: readonly { x: number; z: number }[], scale: number, ox: number, oz: number): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${((l.cx + p.x - ox) * scale).toFixed(1)},${(-(l.cz + p.z - oz) * scale).toFixed(1)}`).join(" ") + " Z";
}

/** Coastline; a drowned shelf charts only the cliff-walled rock standing above the sea. */
function coastPath(l: (typeof LANDMASSES)[number], scale: number, ox: number, oz: number): string {
  if ((l.shore ?? 0.4) <= 0 && l.mesas?.length) return l.mesas.map((m) => outlinePath(l, mesaContour(m, -m.edge / 2, 3), scale, ox, oz)).join(" ");
  return outlinePath(l, coastPolygon(l), scale, ox, oz);
}

export interface MapMarker {
  x: number;
  z: number;
  heading: number;
  label: string;
  kind: "ship" | "self" | "mate" | "treasure" | "loot";
}

/** World chart with live ship/player markers. */
export class WorldMap {
  readonly svg: SVGSVGElement;
  private readonly markers: SVGGElement;
  private readonly questRing: SVGCircleElement;
  private readonly ring: SVGCircleElement | null = null;
  private readonly scale: number;
  private readonly ox: number;
  private readonly oz: number;

  constructor(opts: { royale?: boolean } = {}) {
    const royale = opts.royale === true;
    const s = (this.scale = royale ? 0.3 : 0.08);
    this.ox = royale ? BATTLE_ROYALE.center.x : 0;
    this.oz = royale ? BATTLE_ROYALE.center.z : 0;
    const half = royale ? (BATTLE_ROYALE.ringStart + 50) * s : WORLD.half * s;
    this.svg = el("svg", { viewBox: `${-half - 20} ${-half - 20} ${half * 2 + 40} ${half * 2 + 40}`, role: "img", "aria-label": "World map", class: "chart" });
    el("rect", { x: -half - 20, y: -half - 20, width: half * 2 + 40, height: half * 2 + 40, class: "chart-paper" }, this.svg);
    if (royale) {
      el("circle", { cx: 0, cy: 0, r: BATTLE_ROYALE.ringStart * s, class: "chart-warn" }, this.svg);
      this.ring = el("circle", { cx: 0, cy: 0, r: BATTLE_ROYALE.ringStart * s, class: "chart-ring" }, this.svg);
      const label = el("text", { x: 0, y: -half - 6, class: "chart-small", "text-anchor": "middle" }, this.svg);
      label.textContent = "Battle arena";
    } else {
      // Grid lines and compass.
      for (let g = -WORLD.half; g <= WORLD.half; g += 1000) {
        el("line", { x1: g * s, y1: -half, x2: g * s, y2: half, class: "chart-grid" }, this.svg);
        el("line", { x1: -half, y1: g * s, x2: half, y2: g * s, class: "chart-grid" }, this.svg);
      }
      el("rect", { x: -half, y: -half, width: half * 2, height: half * 2, class: "chart-bound" }, this.svg);
      const warn = (WORLD.half - WORLD.boundaryWarning) * s;
      el("rect", { x: -warn, y: -warn, width: warn * 2, height: warn * 2, class: "chart-warn" }, this.svg);
      const boundLabel = el("text", { x: 0, y: -half - 6, class: "chart-small", "text-anchor": "middle" }, this.svg);
      boundLabel.textContent = "Edge of the Tiny Seas";
      for (const r of ROCKS) el("circle", { cx: r.x * s, cy: -r.z * s, r: Math.max(2.5, r.r * s), class: "chart-rock" }, this.svg);
      for (const l of LANDMASSES) {
        const colors = { tropical: "#c5b979", volcanic: "#6b5a4a", frost: "#e8eef2", marsh: "#7d8f5a", ruins: "#d8c69a" };
        const coast = el("path", { d: coastPath(l, s, 0, 0), class: `chart-land theme-${l.theme}${l.kind === "outpost" ? " outpost" : ""}` }, this.svg);
        coast.style.fill = colors[l.theme];
        const labelX = l.cx * s;
        const labelY = -l.cz * s + (l.kind === "outpost" ? l.radius * s + 20 : -l.radius * s - 8);
        const label = el("text", { x: labelX, y: labelY, class: "chart-label", "text-anchor": labelX > half - 70 ? "end" : labelX < -half + 70 ? "start" : "middle" }, this.svg);
        label.textContent = l.name;
      }
      for (const outpost of OUTPOSTS) {
        const anchorIcon = el("text", { x: outpost.landmass.cx * s, y: -outpost.landmass.cz * s + 5, class: "chart-icon", "text-anchor": "middle" }, this.svg);
        anchorIcon.textContent = "⚓";
      }
    }
    this.questRing = el("circle", { cx: 0, cy: 0, r: 24, class: "chart-quest" }, this.svg);
    this.questRing.style.display = "none";
    // Compass rose.
    const cx = half - 40;
    const cy = half - 40;
    el("circle", { cx, cy, r: 22, class: "chart-compass" }, this.svg);
    el("path", { d: `M${cx},${cy - 20} L${cx + 6},${cy} L${cx},${cy + 20} L${cx - 6},${cy} Z`, class: "chart-needle" }, this.svg);
    const n = el("text", { x: cx, y: cy - 25, class: "chart-small", "text-anchor": "middle" }, this.svg);
    n.textContent = "N";
    this.markers = el("g", {}, this.svg);
  }

  /** Battle royale: current safe-water radius in metres. */
  setRing(radius: number): void {
    this.ring?.setAttribute("r", (radius * this.scale).toFixed(1));
  }

  setQuest(island: IslandId | null): void {
    if (!island) {
      this.questRing.style.display = "none";
      return;
    }
    const l = getIsland(island);
    this.questRing.style.display = "";
    this.questRing.setAttribute("cx", String((l.cx - this.ox) * this.scale));
    this.questRing.setAttribute("cy", String(-(l.cz - this.oz) * this.scale));
    this.questRing.setAttribute("r", String(l.radius * this.scale + 12));
  }

  setMarkers(list: readonly MapMarker[]): void {
    this.markers.replaceChildren();
    const s = this.scale;
    for (const m of list) {
      const x = (m.x - this.ox) * s;
      const y = -(m.z - this.oz) * s;
      const g = el("g", { transform: `translate(${x.toFixed(1)},${y.toFixed(1)}) rotate(${((m.heading * 180) / Math.PI).toFixed(1)})` }, this.markers);
      if (m.kind === "ship") {
        el("path", { d: "M0,-12 L6,6 L0,3 L-6,6 Z", class: "chart-ship" }, g);
      } else if (m.kind === "treasure") {
        const star = el("text", { y: 6, class: "chart-treasure", "text-anchor": "middle" }, g);
        star.textContent = "★";
      } else if (m.kind === "loot") {
        el("circle", { r: 3, class: "chart-loot" }, g);
      } else {
        el("circle", { r: 4, class: m.kind === "self" ? "chart-self" : "chart-mate" }, g);
        el("path", { d: "M0,-9 L3,-3 L-3,-3 Z", class: m.kind === "self" ? "chart-self" : "chart-mate" }, g);
      }
      if (!m.label) continue;
      const t = el("text", { x: x.toFixed(1), y: (y + (m.kind === "ship" ? 22 : -10)).toFixed(1), class: "chart-small", "text-anchor": "middle" }, this.markers);
      t.textContent = m.label;
    }
  }
}

const LANDMARK_GLYPH: Record<Landmark["kind"], string> = {
  lighthouse: "🗼",
  windmill: "✣",
  palmGrove: "🌴",
  twinPeaks: "▲",
  arch: "∩",
  shipwreck: "⛵",
  bigRock: "◆",
  hut: "⌂",
  volcano: "^",
  obsidianSpire: "D",
  iceSpire: "*",
  pineGrove: "T",
  deadTree: "Y",
  stiltHut: "H",
  pillars: "III",
  statue: "S",
  beaconTower: "🔥",
  seaLookout: "⌖",
  stockadeGate: "⛩",
};

/** Authored footpaths; the concealed coastal shortcut is charted after discovery. */
function lanternRoutes(svg: SVGSVGElement, scale: number, ox: number, oz: number, discoveries: number): void {
  const grotto = LANTERN_SITES.find((site) => site.id === "grotto")!;
  for (const path of LANTERN_PATHS) {
    if (/grotto|shortcut/.test(path.id) && !(discoveries & grotto.bit)) continue;
    const d = path.points.map((p, i) => `${i ? "L" : "M"}${((p.x - ox) * scale).toFixed(1)},${(-(p.z - oz) * scale).toFixed(1)}`).join(" ");
    el("path", { d, class: "chart-trail", "stroke-width": Math.max(1.5, path.width * scale) }, svg);
  }
}

const LANTERN_MAP_LABELS = { landing: "Landing beach", cottage: "Keeper cottage", lighthouse: "Lighthouse", overlook: "Gull Rock", wreck: "Mercy's wreck", grotto: "Survivors' grotto" } as const;

function lanternLabels(svg: SVGSVGElement, scale: number, discoveries: number): void {
  for (const site of LANTERN_SITES) {
    if (site.id === "grotto" && !(discoveries & site.bit)) continue;
    const x = (site.x - LANTERN_ORIGIN.x) * scale;
    const y = -(site.z - LANTERN_ORIGIN.z) * scale;
    el("circle", { cx: x, cy: y, r: 3, class: "chart-site" }, svg);
    const label = el("text", { x: x + 5, y: y - 5, class: "chart-site-label", "text-anchor": site.id === "lighthouse" ? "end" : "start" }, svg);
    if (site.id === "lighthouse") label.setAttribute("x", String(x - 5));
    label.textContent = LANTERN_MAP_LABELS[site.id];
    if (site.id === "lighthouse") {
      const inside = el("text", { x: x - 5, y: y + 8, class: "chart-site-detail", "text-anchor": "end" }, svg);
      inside.textContent = "Interior stairs · signal above";
    }
  }
}

/** Exploration chart never marks unclaimed treasure or unread clues. */
export function lanternMapSvg(discoveries: number): SVGSVGElement {
  const island = getIsland("lantern");
  const scale = 1.5;
  const edge = island.radius * scale + 28;
  const svg = el("svg", { viewBox: `${-edge} ${-edge} ${edge * 2} ${edge * 2}`, role: "img", "aria-label": "Lantern Isle footpaths and landmarks", class: "questmap lantern-map" });
  el("rect", { x: -edge, y: -edge, width: edge * 2, height: edge * 2, rx: 12, class: "chart-paper" }, svg);
  el("path", { d: coastPath(island, scale, island.cx, island.cz), class: "chart-land theme-tropical" }, svg).style.fill = "#c5b979";
  lanternRoutes(svg, scale, island.cx, island.cz, discoveries);
  lanternLabels(svg, scale, discoveries);
  const north = el("text", { x: edge - 18, y: -edge + 24, class: "chart-small", "text-anchor": "middle" }, svg);
  north.textContent = "N↑";
  return svg;
}

/** Terraces and elevated crossings make the fortress map legible without revealing loot. */
function skullFortMap(svg: SVGSVGElement, scale: number): void {
  for (const terrace of SKULL_TERRACES) {
    const d = terrace.outline.map((p, i) => `${i ? "L" : "M"}${(p.x * scale).toFixed(1)},${(-p.z * scale).toFixed(1)}`).join(" ") + " Z";
    el("path", { d, class: "chart-fort-terrace" }, svg);
  }
  for (const p of SKULL_ISLE.trees) {
    el("circle", { cx: p.x * scale, cy: -p.z * scale, r: 2, class: "chart-fort-canopy" }, svg);
  }
  for (const path of SKULL_PATHS) {
    const d = path.points.map((p, i) => `${i ? "L" : "M"}${((p.x - SKULL_ORIGIN.x) * scale).toFixed(1)},${(-(p.z - SKULL_ORIGIN.z) * scale).toFixed(1)}`).join(" ");
    el("path", { d, class: path.kind === "bridge" ? "chart-trail chart-fort-bridge" : "chart-trail", "stroke-width": Math.max(1.3, Math.min(2.8, path.width * scale * 0.6)) }, svg);
  }
  for (const tower of SKULL_TOWERS) {
    el("rect", { x: (tower.x - SKULL_ORIGIN.x - tower.width / 2) * scale, y: -(tower.z - SKULL_ORIGIN.z + tower.depth / 2) * scale, width: tower.width * scale, height: tower.depth * scale, class: "chart-fort-tower" }, svg);
  }
}

/** Treasure map card for one island: coastline, landmarks and the X. */
export function questMapSvg(island: IslandId, spotIndex: number, discoveries = 0): SVGSVGElement {
  const l = getIsland(island);
  const spot = l.digSpots[spotIndex];
  const pad = 30;
  const size = l.radius * 1.5;
  const s = 140 / size;
  const svg = el("svg", { viewBox: `${-size * s - pad} ${-size * s - pad} ${size * 2 * s + pad * 2} ${size * 2 * s + pad * 2}`, role: "img", "aria-label": `Treasure map of ${l.name}`, class: "questmap" });
  el("rect", { x: -size * s - pad, y: -size * s - pad, width: size * 2 * s + pad * 2, height: size * 2 * s + pad * 2, rx: 12, class: "chart-paper" }, svg);
  const coast = el("path", { d: coastPath(l, s, l.cx, l.cz), class: `chart-land theme-${l.theme}` }, svg);
  const colors = { tropical: "#c5b979", volcanic: "#6b5a4a", frost: "#e8eef2", marsh: "#7d8f5a", ruins: "#d8c69a" };
  coast.style.fill = colors[l.theme];
  for (const b of l.bumps) {
    if (b.h > 6) el("circle", { cx: b.x * s, cy: -b.z * s, r: b.r * s * 0.5, class: "chart-hill" }, svg);
  }
  for (const m of l.mesas ?? []) {
    if (m.top > 6) el("path", { d: outlinePath(l, mesaContour(m, m.edge, 3), s, l.cx, l.cz), class: "chart-hill" }, svg);
  }
  if (island === "skull") skullFortMap(svg, s);
  if (island !== "skull") for (const p of l.trees) {
    const t = el("text", { x: p.x * s, y: -p.z * s, class: "chart-tree", "text-anchor": "middle" }, svg);
    t.textContent = l.theme === "frost" ? "T" : l.theme === "marsh" ? "Y" : "🌴";
  }
  if (island === "lantern") lanternRoutes(svg, s, l.cx, l.cz, discoveries);
  if (island === "lantern") {
    lanternLabels(svg, s, discoveries);
  } else {
    for (const m of l.landmarks) {
      const t = el("text", { x: m.x * s, y: -m.z * s + 6, class: "chart-icon", "text-anchor": "middle" }, svg);
      t.textContent = LANDMARK_GLYPH[m.kind];
      const label = el("text", { x: m.x * s, y: -m.z * s + 22, class: "chart-small", "text-anchor": "middle" }, svg);
      label.textContent = m.label;
    }
  }
  if (spot) {
    const x = spot.x * s;
    const y = -spot.z * s;
    el("path", { d: `M${x - 9},${y - 9} L${x + 9},${y + 9} M${x + 9},${y - 9} L${x - 9},${y + 9}`, class: "chart-x" }, svg);
  }
  // Point toward the closest service port.
  const outpost = nearestOutpost(l.cx, l.cz);
  const ang = Math.atan2(-(outpost.landmass.cz - l.cz), outpost.landmass.cx - l.cx);
  const ax = Math.cos(ang) * (size * s + 8);
  const ay = Math.sin(ang) * (size * s + 8);
  const arrow = el("text", { x: ax, y: ay, class: "chart-small", "text-anchor": "middle" }, svg);
  arrow.textContent = `→ ${outpost.name}`;
  arrow.setAttribute("transform", `rotate(${(ang * 180) / Math.PI} ${ax} ${ay})`);
  const n = el("text", { x: size * s + pad - 14, y: -size * s - pad + 22, class: "chart-small", "text-anchor": "middle" }, svg);
  n.textContent = "N↑";
  return svg;
}
